import { useState } from "react";
import { toast } from "sonner";
import { Upload, X, Camera, Send } from "lucide-react";
import api, { formatErr, ASSET_BASE } from "@/lib/api";

export default function QuickPhotoUpload() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", make_model: "", serial_number: "", message: "" });
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const fd = new FormData();
    Array.from(files).slice(0, 5).forEach(x => fd.append("files", x));
    try {
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploads(u => [...u, ...r.data.urls].slice(0, 5));
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) return toast.error("Please fill required fields");
    if (uploads.length === 0) return toast.error("Please attach at least one photo");
    setSending(true);
    try {
      await api.post("/enquiries", {
        kind: "contact", name: form.name, phone: form.phone, email: form.email, message: form.message, company: "",
        extra: { reason: "Photo enquiry", address: form.address, make_model: form.make_model, serial_number: form.serial_number, photos: uploads.length, photo_urls: uploads.join(", ") },
      });
      toast.success("Photos sent — we'll be in touch shortly.");
      setForm({ name: "", phone: "", email: "", address: "", make_model: "", serial_number: "", message: "" });
      setUploads([]);
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <section className="bg-[#111111] text-white ff-dotgrid-bg">
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold">Not sure what you need?</div>
          <h2 className="font-display text-4xl md:text-6xl uppercase mt-3 leading-[0.95]">Send us a photo<br/>of your machine<br/>or part</h2>
          <p className="mt-6 text-white/75 max-w-lg">Snap a picture of your forklift, a damaged component, a data plate or the part you're trying to identify. Forkfix will review it and get back to you with next steps — quote, availability or a service booking.</p>
          <div className="mt-8 space-y-3 text-sm text-white/80">
            {["Forklift data plates","Damaged parts","Fault codes / warning lights","Hydraulic leaks or wear","Anything you can't identify"].map(t => (
              <div key={t} className="flex items-center gap-3"><Camera className="w-4 h-4 text-[#D71920] shrink-0" /> {t}</div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="bg-white text-[#111] p-6 md:p-8 border border-white/10" data-testid="photo-upload-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="ff-label">Name*</label><input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="ff-input" data-testid="pu-name" /></div>
            <div><label className="ff-label">Phone*</label><input required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="ff-input" data-testid="pu-phone" /></div>
            <div className="md:col-span-2"><label className="ff-label">Email*</label><input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="ff-input" data-testid="pu-email" /></div>
            <div className="md:col-span-2"><label className="ff-label">Site Address</label><input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="ff-input" placeholder="Where the forklift is located" data-testid="pu-address" /></div>
            <div><label className="ff-label">Make & Model</label><input value={form.make_model} onChange={e => setForm({ ...form, make_model: e.target.value })} className="ff-input" placeholder="e.g. Toyota 8FG25" data-testid="pu-make" /></div>
            <div><label className="ff-label">Serial Number</label><input value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })} className="ff-input" placeholder="From data plate" data-testid="pu-serial" /></div>
            <div className="md:col-span-2"><label className="ff-label">What's in the photo?</label><textarea rows={3} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="e.g. Toyota 8FG25, the lift cylinder is leaking near the base." className="ff-input" data-testid="pu-message" /></div>
            <div className="md:col-span-2">
              <label className="ff-label">Photos* (up to 5)</label>
              <label className="ff-card p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-[#D71920]" data-testid="pu-drop">
                <Upload className="w-6 h-6 text-[#D71920]" />
                <span className="text-sm text-[#333]">Tap to add photos, videos or documents (any format)</span>
                <input type="file" multiple className="hidden" onChange={e => handleUpload(e.target.files)} />
                {uploading && <span className="text-xs uppercase tracking-widest text-[#D71920]">Uploading…</span>}
              </label>
              {uploads.length > 0 && (
                <div className="mt-3 grid grid-cols-3 md:grid-cols-5 gap-2">
                  {uploads.map((u, i) => (
                    <div key={u} className="relative aspect-square border border-[#e5e5e5] bg-[#f4f4f4]">
                      <img src={`${ASSET_BASE}${u}`} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setUploads(uploads.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/70 text-white p-0.5"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button disabled={sending || uploading} className="ff-btn-primary mt-6 disabled:opacity-60" data-testid="pu-submit"><Send className="w-4 h-4" /> {sending ? "Sending…" : "Send Photos"}</button>
        </form>
      </div>
    </section>
  );
}
